// ChargeReceipt.jsx — 1:1급여 계산 풀이(영수증 모양) (2026-10-06 UX)
//   서버가 이미 계산해 보낸 숫자(billingSegments·row)만 식 모양으로 보여 준다 — 화면에서 따로 계산하지 않는다.
//   달 중간에 조건이 바뀐 직원은 기간별로 줄을 나눈다.
import React from 'react';
import { fmtMoney, fmtNum } from './monthlyCalculationUtils';
import { C } from './charge.styles';

const R = {
  box: { border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: 'var(--bg-card)', display: 'flex', flexDirection: 'column', gap: 6 },
  segHead: { marginTop: 4, fontSize: 12, fontWeight: 800, color: 'var(--text-muted)' },
  line: { display: 'grid', gridTemplateColumns: 'minmax(70px, auto) minmax(0, 1fr) auto', gap: 10, alignItems: 'baseline', fontSize: 13 },
  label: { fontWeight: 800, color: 'var(--text)' },
  formula: { color: 'var(--text-muted)', minWidth: 0, overflowWrap: 'anywhere' },
  amount: (minus) => ({ fontWeight: 900, color: minus ? '#dc2626' : 'var(--text)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }),
  total: { marginTop: 4, paddingTop: 8, borderTop: '1px dashed var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' },
  totalLabel: { fontSize: 14, fontWeight: 900, color: 'var(--text)' },
  totalValue: { fontSize: 17, fontWeight: 900, color: 'var(--text)' },
};

function Line({ label, formula, amount }) {
  const minus = Number(amount) < 0;
  return (
    <div style={R.line}>
      <span style={R.label}>{label}</span>
      <span style={R.formula}>{formula}</span>
      <span style={R.amount(minus)}>{minus ? '−' : ''}{fmtMoney(Math.abs(Number(amount || 0)))}</span>
    </div>
  );
}

function incentiveFormula(seg, row, single) {
  const source = seg.incentiveSource || 'direct';
  if (source === 'none') return '인센티브 계산 안 함';
  if (source === 'incentive_total') {
    return `인센티브 관리 금액 ${fmtMoney(seg.incentiveMonthlyTotal)} × 적용 ${fmtNum(seg.incentiveAppliedRatio)}%`;
  }
  const unit = fmtMoney(seg.incentiveUnitPrice);
  if (!single) return `${fmtNum(seg.incentivePayableCount)}장 × ${unit}`;
  const count = Number(row.weightedCount || 0);
  if (count < 0) return `${fmtNum(count)}장 × ${unit} (취소가 더 많아 차감)`;
  const base = Number(seg.incentiveIncludedCount || 0);
  if (base <= 0) return `인정 ${fmtNum(count)}장 × ${unit}`;
  return `(인정 ${fmtNum(count)}장 − 기본 ${fmtNum(base)}장) × ${unit}`;
}

export function ChargeReceipt({ row }) {
  const segs = row?.billingSegments || [];
  const single = segs.length <= 1;
  return (
    <div style={R.box}>
      <div style={C.sectionTitle}>계산 풀이</div>
      {segs.length === 0 && <div style={C.sectionHint}>이 달에 적용된 급여 조건이 없습니다.</div>}
      {segs.map((seg, index) => (
        <React.Fragment key={`${seg.ruleId || 'seg'}-${seg.startDate}-${index}`}>
          {!single && <div style={R.segHead}>{seg.startDate} ~ {seg.endDate} ({seg.days}일) 조건</div>}
          <Line
            label="근무일 급여"
            formula={seg.salaryEnabled ? `월급 ${fmtMoney(seg.monthlySalary)} × ${seg.days}/${row.monthDays}일` : '급여 계산 안 함'}
            amount={seg.proratedSalary}
          />
          {seg.salaryEnabled && seg.leaveDeductionEnabled ? (
            <Line
              label="연차 차감"
              formula={single
                ? `(연차 ${fmtNum(row.leaveDays)}일 − 무료 ${fmtNum(seg.freeLeaveDays)}일) × 일급 ${fmtMoney(row.dailySalary)}`
                : `무료 ${fmtNum(seg.freeLeaveDays)}일 넘는 연차 × 일급`}
              amount={-Number(seg.leaveDeductionAmount || 0)}
            />
          ) : (
            <Line label="연차 차감" formula="연차 차감 안 함" amount={0} />
          )}
          <Line label="인센티브" formula={incentiveFormula(seg, row, single)} amount={seg.incentiveAmount} />
        </React.Fragment>
      ))}
      <div style={R.total}>
        <span style={R.totalLabel}>1:1급여</span>
        <span style={R.totalValue}>{fmtMoney(row?.chargeAmount)}</span>
      </div>
    </div>
  );
}
